import Add from '@mui/icons-material/Add';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Chip } from '~components/Chip/Chip';
import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { ComplianceDocumentType } from '~hooks/useComplianceDocumentTypes/models';
import useComplianceDocumentTypes from '~hooks/useComplianceDocumentTypes/useComplianceDocumentTypes';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { dateFormat } from '~utils/dateTime';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ComplianceDocumentTypeForm, {
  ComplianceDocumentTypeFormHandler,
} from './ComplianceDocumentTypeForm';
import {
  ComplianceDocumentTypeDto,
  ComplianceDocumentTypeDtoAsyncError,
} from './complianceDocumentTypeSchema';

type DialogState = {
  isOpen: boolean;
  isSubmitting: boolean;
  documentType?: ComplianceDocumentType;
};

function renderBooleanIcon(value: boolean) {
  return value ? (
    <Check fontSize="small" titleAccess={t('actions.yes')} />
  ) : (
    <Close
      fontSize="small"
      titleAccess={t('actions.no')}
      sx={{ color: 'text.secondary' }}
    />
  );
}

const ComplianceDocumentTypesManagement = observer(() => {
  const formRef = useRef<ComplianceDocumentTypeFormHandler>(null);
  const [formDialogState, setFormDialogState] = useState<DialogState>({
    isOpen: false,
    isSubmitting: false,
  });
  const [dialogAsyncErrors, setDialogAsyncErrors] = useState<
    ComplianceDocumentTypeDtoAsyncError[]
  >([]);
  const [deleteDialogState, setDeleteDialogState] = useState<DialogState>({
    isOpen: false,
    isSubmitting: false,
  });

  const { userStore, toasterStore } = useStores();
  const {
    isLoading,
    complianceDocumentTypes,
    pagination,
    getAll,
    create,
    update,
    remove,
  } = useComplianceDocumentTypes();
  const companyId = getEffectiveUserCompanyId(userStore);

  const fetchData = useCallback(
    async (link?: PaginationLink) => {
      if (!companyId) return;
      const cursor = link ? pagination[link] : undefined;
      await getAll(companyId, link, cursor);
    },
    [companyId, pagination],
  );

  useEffect(() => {
    if (companyId) {
      fetchData();
    }
  }, [companyId]);

  const handleCreateClick = useCallback(() => {
    setFormDialogState({ isOpen: true, isSubmitting: false });
  }, []);

  const handleDeleteClick = useCallback((documentType: ComplianceDocumentType) => {
    setDeleteDialogState({
      isOpen: true,
      isSubmitting: false,
      documentType,
    });
  }, []);

  const handleCloseFormDialog = useCallback(() => {
    setFormDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleSubmitFormDialog = useCallback(async () => {
    const formData = await formRef.current?.onSubmit();
    if (!formData || !companyId) return;

    const documentTypeId = formDialogState.documentType?.id;

    try {
      setDialogAsyncErrors([]);
      setFormDialogState((state) => ({ ...state, isSubmitting: true }));

      const body: Record<string, unknown> = {
        name: formData.name,
        description: formData.description || '',
        field_type: formData.fieldType,
        has_expiration: formData.hasExpiration,
        dispatch_gating: formData.dispatchGating,
        required_vendors: formData.requiredVendors,
        required_internal_drivers: formData.requiredInternalDrivers,
        required_shared_drivers: formData.requiredSharedDrivers,
        required_equipment: formData.requiredEquipment,
      };

      if (documentTypeId) {
        body.external_id = formData.externalId ?? '';
        const parsed = await update(companyId, documentTypeId, body);
        toasterStore.push(
          alert(
            t('administration.compliance_document_types.type_updated', {
              name: parsed.name,
            }),
            AlertTypes.success,
          ),
        );
      } else {
        if (formData.externalId) {
          body.external_id = formData.externalId;
        }
        const parsed = await create(companyId, body);
        toasterStore.push(
          alert(
            t('administration.compliance_document_types.type_created', {
              name: parsed.name,
            }),
            AlertTypes.success,
          ),
        );
      }

      setFormDialogState({ isOpen: false, isSubmitting: false });
    } catch (error) {
      setFormDialogState((state) => ({
        ...state,
        isSubmitting: false,
      }));

      if (axios.isAxiosError(error)) {
        const errorsArray = (error.response?.data?.error?.errors ??
          []) as ModelError_Item[];

        const asyncErrors: ComplianceDocumentTypeDtoAsyncError[] = [];
        const fieldMap: Record<string, keyof ComplianceDocumentTypeDto> = {
          name: 'name',
          external_id: 'externalId',
        };

        errorsArray.forEach((item) => {
          const { field, message } = item;
          const formField = fieldMap[field];
          if (formField) {
            asyncErrors.push({ field: formField, message });
          }
        });

        if (asyncErrors.length) {
          setDialogAsyncErrors(asyncErrors);
        }
      }
    }
  }, [formDialogState.documentType?.id, companyId]);

  const handleDelete = useCallback(async () => {
    const documentTypeId = deleteDialogState.documentType?.id;
    if (!documentTypeId || !companyId) return;

    try {
      setDeleteDialogState((state) => ({
        ...state,
        isSubmitting: true,
      }));
      await remove(companyId, documentTypeId);
      toasterStore.push(
        alert(
          t('administration.compliance_document_types.type_deleted'),
          AlertTypes.success,
        ),
      );
      setDeleteDialogState({ isOpen: false, isSubmitting: false });
    } catch {
      setDeleteDialogState((state) => ({
        ...state,
        isSubmitting: false,
      }));
    }
  }, [deleteDialogState.documentType?.id, companyId]);

  const columns = useMemo<Array<DataTableColumn<ComplianceDocumentType>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        valueGetter: (documentType) => documentType.name,
      },
      {
        field: 'externalId',
        headerName: t('form_fields.external_id'),
        valueGetter: (documentType) => documentType.externalId || '—',
      },
      {
        field: 'requiredVendors',
        headerName: t('administration.compliance_document_types.req_vendors'),
        renderCell: (documentType) => renderBooleanIcon(documentType.requiredVendors),
      },
      {
        field: 'requiredInternalDrivers',
        headerName: t('administration.compliance_document_types.req_internal_drivers'),
        renderCell: (documentType) =>
          renderBooleanIcon(documentType.requiredInternalDrivers),
      },
      {
        field: 'requiredSharedDrivers',
        headerName: t('administration.compliance_document_types.req_shared_drivers'),
        renderCell: (documentType) =>
          renderBooleanIcon(documentType.requiredSharedDrivers),
      },
      {
        field: 'requiredEquipment',
        headerName: t('administration.compliance_document_types.req_equipment'),
        renderCell: (documentType) => renderBooleanIcon(documentType.requiredEquipment),
      },
      {
        field: 'hasExpiration',
        headerName: t('administration.compliance_document_types.has_expiration'),
        renderCell: (documentType) => renderBooleanIcon(documentType.hasExpiration),
      },
      {
        field: 'fieldType',
        headerName: t('administration.compliance_document_types.field_type'),
        renderCell: (documentType) => (
          <Chip color="default" label={documentType.fieldType} hideDropdownArrow />
        ),
      },
      {
        field: 'dispatchGating',
        headerName: t('administration.compliance_document_types.dispatch_gating'),
        renderCell: (documentType) => renderBooleanIcon(documentType.dispatchGating),
      },
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        valueGetter: (documentType) => dateFormat(documentType.createdAt),
      },
      actionsColumn((documentType) => {
        const options = [
          {
            title: t('actions.delete'),
            icon: <Delete />,
            callBack: () => handleDeleteClick(documentType),
          },
        ];
        return (
          <>
            <IconButton
              size="small"
              onClick={() => {
                setFormDialogState({
                  isOpen: true,
                  isSubmitting: false,
                  documentType,
                });
              }}
            >
              <Edit />
            </IconButton>
            <SimpleMenu options={options}>
              <MoreHoriz />
            </SimpleMenu>
          </>
        );
      }),
    ],
    [],
  );

  useEffect(() => {
    if (!formDialogState.isOpen) {
      setDialogAsyncErrors([]);
    }
  }, [formDialogState.isOpen]);

  return (
    <>
      <SettingsPageHeader
        title={t('administration.compliance_document_types.title')}
        description={t('settings.page_descriptions.document_types')}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <SmallButton
          color="brandV2Yellow"
          onClick={handleCreateClick}
          startIcon={<Add />}
        >
          {t('administration.compliance_document_types.create_type')}
        </SmallButton>
      </Box>
      <Box data-test-id="compliance-document-types-grid">
        <DataTable
          id="compliance-document-types-data-grid-view"
          columns={columns}
          rows={complianceDocumentTypes}
          loading={isLoading}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isLoading}
              onNavigate={fetchData}
              pagination={pagination}
            />
          }
        />
      </Box>

      <Dialog
        isOpen={formDialogState.isOpen}
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={handleCloseFormDialog}>
          {formDialogState.documentType
            ? t('administration.compliance_document_types.update_type')
            : t('administration.compliance_document_types.create_type')}
        </DialogHeader>

        <DialogContent>
          <ComplianceDocumentTypeForm
            key={formDialogState.documentType?.id ?? 'create'}
            ref={formRef}
            asyncErrors={dialogAsyncErrors}
            documentType={formDialogState.documentType}
          />
        </DialogContent>

        <DialogActions>
          {Boolean(dialogAsyncErrors.length) && (
            <ShakyErrorIndicator
              message={t('administration.users.user_form.invalid_data_error_messages')}
            />
          )}
          <SmallButton
            color="secondary"
            disabled={formDialogState.isSubmitting}
            onClick={handleCloseFormDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>
          <SmallLoadingButton
            loading={formDialogState.isSubmitting}
            onClick={handleSubmitFormDialog}
          >
            {formDialogState.documentType ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        isBusy={deleteDialogState.isSubmitting}
        isOpen={deleteDialogState.isOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
        title={t('administration.compliance_document_types.delete_type_title')}
      >
        <Typography>
          {t('administration.compliance_document_types.delete_type_text')}
        </Typography>
      </ConfirmationDialog>
    </>
  );
});

export { ComplianceDocumentTypesManagement };
